/** @jsx jsx */

import { useState } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import { copyTextToClipboard } from '@zapier/common-utils';
import {
  BetaMenuTriggerRoot,
  BetaMenuTriggerPopover,
  BetaMenuTriggerList,
  BetaMenuTriggerItem,
  IconButton,
  ModalDialogRoot,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
} from '@zapier/design-system';
import { jsx } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import { Status } from 'app/zap-templates/types';
import { useNotify } from 'app/developer-v3/hooks';
import { useDeleteZapTemplate } from 'app/zap-templates/hooks';
import {
  getZapTemplateEditUrl,
  getCreateTestZapUrl,
} from 'app/zap-templates/utils';

type Props = {
  id: string;
  landingPageUrl: string;
  status: Status;
  title: string;
};

function ActionsMenu({ id, landingPageUrl, status, title }: Props) {
  const [isAboutDeleting, setIsAboutDeleting] = useState(false);

  const notify = useNotify();
  const {
    mutate: deleteZapTemplate,
    isMutating: isDeleting,
  } = useDeleteZapTemplate();

  const editUrl = getZapTemplateEditUrl(id);
  const createTestZapUrl = getCreateTestZapUrl(id);

  return (
    <span>
      <BetaMenuTriggerRoot>
        <IconButton
          aria-label="More actions"
          name="navMoreHoriz"
          variant="ghost"
          size="small"
        />
        <BetaMenuTriggerPopover>
          <BetaMenuTriggerList
            aria-label="Manage Zap templates"
            onAction={key => {
              switch (key) {
                case 'edit':
                  window.open(editUrl.href, '_blank');
                  break;
                case 'create-zap':
                  window.open(createTestZapUrl.href, '_blank');
                  break;
                case 'copy-link':
                  copyTextToClipboard(createTestZapUrl.href);
                  notify.success('Copied!');
                  break;
                case 'view-landing':
                  window.open(landingPageUrl, '_blank');
                  break;
                case 'copy-id':
                  copyTextToClipboard(id);
                  notify.success('Copied!');
                  break;
                case 'delete':
                  setIsAboutDeleting(true);
                  break;
              }
            }}
            disabledKeys={isDeleting ? ['delete'] : []}
          >
            <BetaMenuTriggerItem id="edit">
              <Icon name="actionEdit" size={20} />
              Edit
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="create-zap">
              <Icon name="miscBoltAlt" size={20} />
              Create Zap
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="copy-link">
              <Icon name="actionLink" size={20} />
              Copy Link
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="view-landing">
              <Icon name="fileGuide" size={20} />
              View Landing Page
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="copy-id">
              <Icon name="actionCopy" size={20} />
              Copy ID ({id})
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="delete">
              <Icon name="actionTrash" size={20} />
              Delete
            </BetaMenuTriggerItem>
          </BetaMenuTriggerList>
        </BetaMenuTriggerPopover>
      </BetaMenuTriggerRoot>
      <ModalDialogRoot
        isOpen={isAboutDeleting}
        onOpenChange={setIsAboutDeleting}
      >
        <ModalDialogHeader>Delete Zap template</ModalDialogHeader>
        <ModalDialogBody>
          Are you sure you want to delete the Zap template? ({title || id})
        </ModalDialogBody>
        <ModalDialogFooter>
          <AsyncButton
            variant="danger"
            isWorking={isDeleting}
            onPress={async () => {
              await deleteZapTemplate({ id, status });
              setIsAboutDeleting(false);
            }}
            size="small"
          >
            Yes, delete
          </AsyncButton>
        </ModalDialogFooter>
      </ModalDialogRoot>
    </span>
  );
}

export default ActionsMenu;
